import { authFetch, buildApiUrl } from '@/services/api/client';

const CONTROL_TOWER_TIMEOUT_MS = 15000;
const CONTROL_TOWER_LKG_MAX_AGE_MS = 10 * 60_000;
const CONTROL_TOWER_LKG_PREFIX = 'control-tower-lkg:';

export interface ControlTowerError {
  message: string;
}

export interface ControlTowerCriticalPathCheck {
  key: string;
  severity: string | null;
  status: 'ok' | 'warn' | 'error' | 'unknown';
  last_ok_at: string | null;
  last_error_at: string | null;
  message: string | null;
}

export interface ControlTowerSystemGraphSummary {
  counts: Record<string, number>;
  generated_at: string | null;
  worst_freshness: {
    node_id: string | null;
    freshness_s: number | null;
    last_ok_at: string | null;
    status: string | null;
  } | null;
  last_oracle_run: string | null;
  last_rate_push: string | null;
  error?: ControlTowerError | null;
}

export interface ControlTowerLearningSummary {
  learnable_pct: number | null;
  stale_inputs: Array<{
    property_id: string | null;
    input_type: string | null;
    quality: string | null;
    hours_stale: number | null;
  }>;
  outcome_coverage: {
    total_decisions: number;
    tracked_decisions: number;
    coverage_pct: number;
  };
  error?: ControlTowerError | null;
}

export interface ControlTowerTraceSummary {
  reachable: boolean;
  last_day_available: string | null;
  error?: ControlTowerError | null;
}

export interface ControlTowerResponse {
  generated_at: string;
  property_id: string | null;
  days: number;
  system_graph_summary: ControlTowerSystemGraphSummary;
  critical_path_checks: ControlTowerCriticalPathCheck[];
  learning_summary: ControlTowerLearningSummary;
  trace_summary: ControlTowerTraceSummary;
  client_fallback?: {
    type: 'cached_lkg';
    reason: string;
    fallback_at: string;
  } | null;
}

type ControlTowerLkgEntry = {
  response: ControlTowerResponse;
  cachedAtMs: number;
};

export class ControlTowerUnavailableError extends Error {
  propertyId: string | null;
  days: number;

  constructor(options: { propertyId: string | null; days: number; reason: string }) {
    super(
      `Control Tower unavailable for ${options.propertyId ?? 'auto'} (${options.days}d): ${options.reason}`,
    );
    this.name = 'ControlTowerUnavailableError';
    this.propertyId = options.propertyId;
    this.days = options.days;
  }
}

function lkgKey(propertyId: string | null, days: number): string {
  return `${CONTROL_TOWER_LKG_PREFIX}${propertyId ?? '__auto__'}::${days}`;
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function writeLkg(propertyId: string | null, days: number, response: ControlTowerResponse): void {
  if (!canUseSessionStorage()) return;
  try {
    const entry: ControlTowerLkgEntry = { response, cachedAtMs: Date.now() };
    window.sessionStorage.setItem(lkgKey(propertyId, days), JSON.stringify(entry));
  } catch {
    // Best-effort cache only.
  }
}

function readLkg(propertyId: string | null, days: number): ControlTowerResponse | null {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(lkgKey(propertyId, days));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ControlTowerLkgEntry>;
    if (typeof parsed.cachedAtMs !== 'number' || !parsed.response) {
      window.sessionStorage.removeItem(lkgKey(propertyId, days));
      return null;
    }
    if (Date.now() - parsed.cachedAtMs > CONTROL_TOWER_LKG_MAX_AGE_MS) {
      window.sessionStorage.removeItem(lkgKey(propertyId, days));
      return null;
    }
    return parsed.response;
  } catch {
    window.sessionStorage.removeItem(lkgKey(propertyId, days));
    return null;
  }
}

function withFallback(response: ControlTowerResponse, reason: string): ControlTowerResponse {
  return {
    ...response,
    client_fallback: {
      type: 'cached_lkg',
      reason,
      fallback_at: new Date().toISOString(),
    },
  };
}

function toReason(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  return 'Unknown error';
}

export async function fetchControlTower(
  options: { propertyId?: string | null; days?: number } = {},
): Promise<ControlTowerResponse> {
  const propertyId = options.propertyId ?? null;
  const days = options.days ?? 7;

  try {
    const response = await authFetch(
      buildApiUrl('/health/control-tower', {
        days,
        property_id: propertyId ?? undefined,
      }),
      { timeout: CONTROL_TOWER_TIMEOUT_MS },
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(
        errorText
          ? `Failed to fetch control tower (HTTP ${response.status}): ${errorText}`
          : `Failed to fetch control tower (HTTP ${response.status})`,
      );
    }

    const payload = (await response.json()) as ControlTowerResponse;
    writeLkg(propertyId, days, payload);
    return payload;
  } catch (error) {
    const cached = readLkg(propertyId, days);
    if (cached) {
      return withFallback(cached, toReason(error));
    }
    throw new ControlTowerUnavailableError({
      propertyId,
      days,
      reason: toReason(error),
    });
  }
}
